import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Eye, EyeOff, Mail } from "lucide-react";
import axios from "axios";
import { EASE_OUT } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const csKey = (slug) => `nn-cs-${slug}-unlocked`;

export const isCaseStudyUnlocked = (slug) => {
  if (typeof window === "undefined") return false;
  // Legacy: users who unlocked the whole site under the old site-wide gate
  // get NDA case studies unlocked automatically.
  if (localStorage.getItem("nn-portfolio-unlocked") === "1") {
    localStorage.setItem(csKey(slug), "1");
    return true;
  }
  return localStorage.getItem(csKey(slug)) === "1";
};

const CaseStudyGate = ({ project, onUnlock }) => {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [shakeKey, setShakeKey] = useState(0);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || status === "loading") return;
    setStatus("loading");
    setError("");
    try {
      const res = await axios.post(`${API}/gate/verify`, { password });
      if (res.data?.ok) {
        localStorage.setItem(csKey(project.slug), "1");
        onUnlock();
      } else {
        throw new Error("denied");
      }
    } catch (err) {
      setStatus("error");
      setError("Incorrect password. Try again, or request access below.");
      setShakeKey((k) => k + 1);
      setPassword("");
      setTimeout(() => setStatus("idle"), 1200);
    }
  };

  const subject = encodeURIComponent(`Case study access — ${project.title}`);
  const body = encodeURIComponent(
    `Hi Nirav,\n\nI'd love access to the "${project.title}" case study. A quick bit about me and where I found the portfolio:\n\n— \n\nThanks!\n`
  );

  return (
    <section
      className="relative py-16 md:py-24 border-t border-[color:var(--nn-border)]"
      data-testid={`case-study-gate-${project.slug}`}
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <motion.div
          key={shakeKey}
          initial={{ opacity: 0, y: 20 }}
          animate={
            status === "error"
              ? { opacity: 1, y: 0, x: [-10, 10, -8, 8, -4, 4, 0] }
              : { opacity: 1, y: 0, x: 0 }
          }
          transition={{ duration: 0.6, ease: EASE_OUT }}
          className="relative rounded-3xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/60 backdrop-blur-xl overflow-hidden"
        >
          {/* Ambient orb */}
          <div
            aria-hidden
            className="absolute -top-40 -right-40 w-[60vw] max-w-[600px] aspect-square rounded-full opacity-40 blur-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(223,255,28,0.35), transparent 70%)",
            }}
          />

          <div className="relative grid md:grid-cols-12 gap-10 p-8 md:p-14">
            {/* Left — story */}
            <div className="md:col-span-6">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-9 h-9 rounded-full grid place-items-center bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)]">
                  <Lock size={16} />
                </span>
                <span className="chip chip-accent">NDA · locked case study</span>
              </div>
              <h2 className="h-display text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-5">
                A <span className="font-serif-i text-[color:var(--nn-muted)]">private</span>{" "}
                deep-dive.
              </h2>
              <p className="text-base md:text-lg text-[color:var(--nn-text)]/85 leading-relaxed max-w-md mb-6">
                This case study is under an active client NDA. If you're evaluating me for a role or
                a project, I'll happily send you the walk-through link and answer questions live.
              </p>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2">
                What you'll see once unlocked
              </div>
              <ul className="text-sm md:text-base text-[color:var(--nn-text)]/80 space-y-1.5">
                <li>→ Full 7-chapter narrative with process, research, and reflections</li>
                <li>→ High-fidelity screens, prototypes, and motion decisions</li>
                <li>→ Shipped metrics and honest what-I'd-do-differently</li>
              </ul>
            </div>

            {/* Right — form */}
            <div className="md:col-span-6 md:pl-8 md:border-l md:border-[color:var(--nn-border)]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                Have the password?
              </div>
              <form onSubmit={onSubmit} data-testid={`gate-form-${project.slug}`}>
                <div className="relative mb-3">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                    placeholder="Enter passphrase…"
                    data-testid={`gate-input-${project.slug}`}
                    className="w-full bg-transparent border-b border-[color:var(--nn-border)] py-3 pr-10 text-lg tracking-widest focus:outline-none focus:border-[color:var(--nn-accent)] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    data-cursor="hover"
                    aria-label={showPw ? "Hide" : "Show"}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-[color:var(--nn-muted)] hover:text-[color:var(--nn-text)] transition-colors"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {error && (
                  <div
                    className="text-[13px] text-[#ff8a8a] font-mono mb-3"
                    data-testid={`gate-error-${project.slug}`}
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || !password.trim()}
                  data-cursor="dragging"
                  data-cursor-label="unlock"
                  data-testid={`gate-submit-${project.slug}`}
                  className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium hover:gap-4 transition-all disabled:opacity-60"
                >
                  {status === "loading" ? "Unlocking…" : "Unlock case study"}
                  <span className="w-9 h-9 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)]">
                    <ArrowRight size={16} />
                  </span>
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-[color:var(--nn-border)]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-3">
                  Don't have the password?
                </div>
                <p className="text-sm text-[color:var(--nn-text)]/80 leading-relaxed mb-4 max-w-sm">
                  I usually reply within 24 hours. Include a short line about you and I'll share
                  the access.
                </p>
                <a
                  href={`mailto:niravk.naik@gmail.com?subject=${subject}&body=${body}`}
                  data-cursor="hover"
                  data-testid={`gate-request-${project.slug}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--nn-border)] hover:border-[color:var(--nn-accent)] transition-colors text-sm"
                >
                  <Mail size={14} /> Request access — niravk.naik@gmail.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudyGate;
